import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

export interface DoaApproverFormValue {
  businessUnit: string;
  region: string;
  rangeLevel: number;
  active: boolean;
  approver: string;
  serialOrder: number;
  approvalType: string;
}

/**
 * "DOA Approver Level - New/Edit" entry form, shown on the DOA Approver Level
 * tab in place of the generic refine panel. The filter dropdown row lives in
 * the catalog table (`quickFilters`), not here. Save emits the form value so
 * the parent stages the row through the normal catalog save flow. Styled by
 * domain-catalogs.component.scss (encapsulation: None).
 */
@Component({
  selector: 'app-catalog-doa-approver-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './catalog-doa-approver-form.component.html',
})
export class CatalogDoaApproverFormComponent {
  @Input() businessUnitOptions: string[] = [];
  @Input() regionOptions: string[] = [];
  @Input() rangeLevelOptions: number[] = [1, 2, 3, 4, 5, 6];
  @Input() serialOrderOptions: number[] = [1, 2, 3, 4, 5];
  @Input() approvalTypeOptions: string[] = ['Finance', 'Commercial', 'Operations', 'Legal'];
  @Input() recordCount = 0;
  @Output() save = new EventEmitter<DoaApproverFormValue>();
  @Output() formReset = new EventEmitter<void>();

  @Input() set editValue(value: DoaApproverFormValue | null) {
    if (value) this.form = { ...value };
  }

  form: DoaApproverFormValue = {
    businessUnit: '',
    region: '',
    rangeLevel: 1,
    active: true,
    approver: '',
    serialOrder: 1,
    approvalType: 'Finance',
  };

  @Input() set defaultBusinessUnit(value: string | null | undefined) {
    if (value && !this.form.businessUnit) this.form.businessUnit = value;
  }

  @Input() set defaultRegion(value: string | null | undefined) {
    if (value && !this.form.region) this.form.region = value;
  }

  get canSave(): boolean {
    return (
      !!this.form.businessUnit &&
      !!this.form.region &&
      !!this.form.approver.trim() &&
      !!this.form.approvalType
    );
  }

  submit(): void {
    if (!this.canSave) return;
    this.save.emit({ ...this.form, approver: this.form.approver.trim() });
    this.reset();
  }

  /** Clears the form back to its default values and returns to add-new mode. */
  resetForm(): void {
    this.reset();
    this.formReset.emit();
  }

  private reset(): void {
    this.form = {
      businessUnit: '',
      region: '',
      rangeLevel: 1,
      active: true,
      approver: '',
      serialOrder: 1,
      approvalType: 'Finance',
    };
  }
}
